import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Info, Sparkles, Clock, ArrowDown, ArrowUp, Cake, DollarSign, CheckCircle, 
  Calendar, Zap, ClipboardCheck, XCircle, MapPin, Phone, Globe, Users, ExternalLink, AlertCircle,
  RotateCcw, FileText, Mail, Star, Tent, Plus, Check
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { parseLocalDate } from '@/utils/dateHelpers';
import { formatCampDisplayName } from '@/utils/campNameFormatter';
import { format } from 'date-fns';
import { getCampSessions } from '@/services/campHierarchyService';
import { getCurrentUser } from '@/lib/supabase';
import { supabase } from '@/lib/supabase';
import AddToPlanModal from '@/components/modals/AddToPlanModal';
import AddSessionModal from '@/components/programs/modal/AddSessionModal';
import { useToast } from '@/components/ui/use-toast';

/**
 * Public Information Section Component
 * 
 * Displays publicly available information about the program
 * Uses a consistent section pattern: Icon + Title + Border + Content
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp data
 * @param {string} [props.error] - Error message to display (optional)
 * @param {Object} [props.userProgram] - User program data (for accessing program_data fields)
 * @param {boolean} [props.showOverviewOnly] - If true, only show overview content (About, Location). If false, show all details.
 * @param {Object} [props.profile] - User profile with children data
 */
const PublicInfoSection = ({ camp, error, userProgram = null, showOverviewOnly = false, profile = null }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // State for camp sessions from camp_audit_data
  const [communitySessions, setCommunitySessions] = useState([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  // State for Add to Plan modal
  const [showAddToPlanModal, setShowAddToPlanModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  // State for Add Session modal
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);

  // Fetch sessions from camp_audit_data (use place_id for Google-sourced camps, id for user-created)
  const parentIdForSessions = camp?.place_id || camp?.program_data?.place_id || camp?.id;

  useEffect(() => {
    if (showOverviewOnly || !parentIdForSessions) return;

    // Only load sessions if this is a parent camp (not a session itself)
    if (camp.hierarchy_level === 'session') {
      setCommunitySessions([]);
      setIsLoadingSessions(false);
      return;
    }

    const fetchCampSessions = async () => {
      setIsLoadingSessions(true);
      try {
        console.log('🔍 Fetching sessions for parent:', {
          parentName: camp?.name,
          parentId: parentIdForSessions,
          usingField: camp?.place_id ? 'place_id' : 'id',
        });
        // Load from camp_audit_data (community sessions) - query by parent_place_id
        const sessions = await getCampSessions(parentIdForSessions, { includeExpired: false });
        
        // Also load user's optimized sessions from user_camp_sessions (legacy: camp_id = parent place_id)
        let userOptimizedSessions = [];
        try {
          const user = await getCurrentUser();
          if (user?.id && parentIdForSessions) {
            const { data: optimizedSessions } = await supabase
              .from('user_camp_sessions')
              .select('*')
              .eq('user_id', user.id)
              .eq('camp_id', parentIdForSessions)
              .order('user_camp_start_date', { ascending: true, nullsFirst: false });
            
            if (optimizedSessions) {
              userOptimizedSessions = optimizedSessions.map(s => ({
                id: s.id,
                session: s.session_name,
                session_name: s.session_name,
                session_year: s.session_year,
                start_date: s.user_camp_start_date,
                end_date: s.user_camp_end_date,
                user_camp_start_date: s.user_camp_start_date,
                user_camp_end_date: s.user_camp_end_date,
                price: s.cost,
                session_price: s.cost,
                cost: s.cost,
                place_id: camp?.place_id || parentIdForSessions,
                from_user_camp_sessions: true
              }));
            }
          }
        } catch (userSessionsErr) {
          console.warn('Error loading user optimized sessions:', userSessionsErr);
        }

        if (sessions?.length !== undefined) {
          console.log(`✅ Found ${sessions.length} sessions`);
        }

        // Merge and deduplicate
        const allSessions = [...(sessions || []), ...userOptimizedSessions];
        const unique = Array.from(
          new Map(allSessions.map(s => {
            const sessionName = s.session || s.session_name || '';
            const sessionYear = s.session_year || '';
            return [`${sessionName}-${sessionYear}`, s];
          })).values()
        );
        
        // Check which sessions are already in user's plan (user_programs + user_camp_sessions with camp_id = session.id)
        let plannedSessionsMap = new Map();
        let plannedCampIds = new Set(); // session ids from user_camp_sessions (Phase 1: camp_id = session.id)
        try {
          const user = await getCurrentUser();
          if (user?.id) {
            // user_camp_sessions: camp_id is the session's id
            const sessionIds = unique.map(s => s.id).filter(Boolean);
            if (sessionIds.length > 0) {
              const { data: onPlanRows } = await supabase
                .from('user_camp_sessions')
                .select('camp_id, id')
                .eq('user_id', user.id)
                .in('camp_id', sessionIds);
              if (onPlanRows?.length) {
                onPlanRows.forEach(r => {
                  plannedCampIds.add(r.camp_id);
                  plannedSessionsMap.set(r.camp_id, { id: r.id, status: 'considering' });
                });
              }
            }
            // Legacy: user_programs sessions under parent
            let baseCampProgramId = null;
            if (userProgram?.id) {
              baseCampProgramId = userProgram.id;
            } else if (parentIdForSessions) {
              const { data: baseCamp } = await supabase
                .from('user_programs')
                .select('id')
                .eq('user_id', user.id)
                .eq('program_data->>place_id', parentIdForSessions)
                .is('parent_camp_id', null)
                .maybeSingle();

              if (baseCamp) {
                baseCampProgramId = baseCamp.id;
              }
            }

            if (baseCampProgramId) {
              const { data: plannedSessions } = await supabase
                .from('user_programs')
                .select('id, program_data, status')
                .eq('user_id', user.id)
                .eq('parent_camp_id', baseCampProgramId);

              if (plannedSessions) {
                plannedSessions.forEach(ps => {
                  const pd = typeof ps.program_data === 'string'
                    ? JSON.parse(ps.program_data)
                    : ps.program_data || {};
                  const sessionName = pd.session || pd.session_name || '';
                  const sessionYear = pd.session_year || '';
                  if (sessionName && sessionYear) {
                    plannedSessionsMap.set(`${sessionName}-${sessionYear}`, {
                      id: ps.id,
                      status: ps.status
                    });
                  }
                });
              }
            }
          }
        } catch (plannedErr) {
          console.warn('Error checking planned sessions:', plannedErr);
        }

        // Format sessions for display
        const formattedSessions = unique.map(session => {
          const sessionName = session.session || session.session_name;
          const sessionYear = session.session_year || '';
          const sessionKey = `${sessionName}-${sessionYear}`;
          const isPlannedByKey = plannedSessionsMap.has(sessionKey);
          const isPlannedById = session.id && plannedCampIds.has(session.id);
          const isPlanned = isPlannedByKey || isPlannedById;
          const plannedData = isPlanned
            ? (plannedSessionsMap.get(session.id) || plannedSessionsMap.get(sessionKey))
            : null;
          
          return {
            ...session,
            sessionName,
            startDate: session.start_date || session.user_camp_start_date,
            endDate: session.end_date || session.user_camp_end_date,
            price: session.session_price || session.price || session.cost,
            sessionYear,
            place_id: session.place_id || camp?.place_id || parentIdForSessions,
            id: session.id,
            isPlanned,
            plannedStatus: plannedData?.status,
            plannedId: plannedData?.id
          };
        });
        
        setCommunitySessions(formattedSessions);
      } catch (err) {
        console.error('Error fetching camp sessions:', err);
        setCommunitySessions([]);
      } finally {
        setIsLoadingSessions(false);
      }
    };
    
    fetchCampSessions();
  }, [parentIdForSessions, camp?.place_id, camp?.name, camp?.hierarchy_level, showOverviewOnly, userProgram?.id]);

  const handleSessionAdded = async (newSession) => {
    // Refresh sessions list - fetch from both sources like initial load
    if (!parentIdForSessions) return;

    try {
      setIsLoadingSessions(true);

      // Load from camp_audit_data (community sessions) - use place_id for query
      const sessions = await getCampSessions(parentIdForSessions, { includeExpired: false });

      // Also load user's optimized sessions from user_camp_sessions (legacy: camp_id = parent id)
      let userOptimizedSessions = [];
      try {
        const user = await getCurrentUser();
        if (user?.id && parentIdForSessions) {
          const { data: optimizedSessions } = await supabase
            .from('user_camp_sessions')
            .select('*')
            .eq('user_id', user.id)
            .eq('camp_id', parentIdForSessions)
            .order('user_camp_start_date', { ascending: true, nullsFirst: false });
          
          if (optimizedSessions) {
            userOptimizedSessions = optimizedSessions.map(s => ({
              id: s.id,
              session: s.session_name,
              session_name: s.session_name,
              session_year: s.session_year,
              start_date: s.user_camp_start_date,
              end_date: s.user_camp_end_date,
              user_camp_start_date: s.user_camp_start_date,
              user_camp_end_date: s.user_camp_end_date,
              price: s.cost,
              session_price: s.cost,
              cost: s.cost,
              place_id: camp?.place_id || parentIdForSessions,
              from_user_camp_sessions: true
            }));
          }
        }
      } catch (userSessionsErr) {
        console.warn('Error loading user optimized sessions:', userSessionsErr);
      }

      // Merge and deduplicate (same logic as initial load)
      const allSessions = [...(sessions || []), ...userOptimizedSessions];
      const unique = Array.from(
        new Map(allSessions.map(s => {
          const sessionName = s.session || s.session_name || '';
          const sessionYear = s.session_year || '';
          return [`${sessionName}-${sessionYear}`, s];
        })).values()
      );
      
      // Check which sessions are already in user's plan (same as initial load)
      let plannedSessionsMap = new Map();
      let plannedCampIds = new Set();
      try {
        const user = await getCurrentUser();
        if (user?.id) {
          const sessionIds = unique.map(s => s.id).filter(Boolean);
          if (sessionIds.length > 0) {
            const { data: onPlanRows } = await supabase
              .from('user_camp_sessions')
              .select('camp_id, id')
              .eq('user_id', user.id)
              .in('camp_id', sessionIds);
            if (onPlanRows?.length) {
              onPlanRows.forEach(r => {
                plannedCampIds.add(r.camp_id);
                plannedSessionsMap.set(r.camp_id, { id: r.id, status: 'considering' });
              });
            }
          }
          let baseCampProgramId = null;
          if (userProgram?.id) {
            baseCampProgramId = userProgram.id;
          } else if (parentIdForSessions) {
            const { data: baseCamp } = await supabase
              .from('user_programs')
              .select('id')
              .eq('user_id', user.id)
              .eq('program_data->>place_id', parentIdForSessions)
              .is('parent_camp_id', null)
              .maybeSingle();

            if (baseCamp) {
              baseCampProgramId = baseCamp.id;
            }
          }

          if (baseCampProgramId) {
            const { data: plannedSessions } = await supabase
              .from('user_programs')
              .select('id, program_data, status')
              .eq('user_id', user.id)
              .eq('parent_camp_id', baseCampProgramId);

            if (plannedSessions) {
              plannedSessions.forEach(ps => {
                const pd = typeof ps.program_data === 'string'
                  ? JSON.parse(ps.program_data)
                  : ps.program_data || {};
                const sessionName = pd.session || pd.session_name || '';
                const sessionYear = pd.session_year || '';
                if (sessionName && sessionYear) {
                  plannedSessionsMap.set(`${sessionName}-${sessionYear}`, {
                    id: ps.id,
                    status: ps.status
                  });
                }
              });
            }
          }
        }
      } catch (plannedErr) {
        console.warn('Error checking planned sessions:', plannedErr);
      }

      // Format sessions for display
      const formattedSessions = unique.map(session => {
        const sessionName = session.session || session.session_name;
        const sessionYear = session.session_year || '';
        const sessionKey = `${sessionName}-${sessionYear}`;
        const isPlannedByKey = plannedSessionsMap.has(sessionKey);
        const isPlannedById = session.id && plannedCampIds.has(session.id);
        const isPlanned = isPlannedByKey || isPlannedById;
        const plannedData = isPlanned
          ? (plannedSessionsMap.get(session.id) || plannedSessionsMap.get(sessionKey))
          : null;

        return {
          ...session,
          sessionName,
          startDate: session.start_date || session.user_camp_start_date,
          endDate: session.end_date || session.user_camp_end_date,
          price: session.session_price || session.price || session.cost,
          sessionYear,
          place_id: session.place_id || camp?.place_id || parentIdForSessions,
          id: session.id,
          isPlanned,
          plannedStatus: plannedData?.status,
          plannedId: plannedData?.id
        };
      });
      
      setCommunitySessions(formattedSessions);
    } catch (err) {
      console.error('Error refreshing sessions:', err);
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Use parseLocalDate to prevent timezone conversion issues
      const date = parseLocalDate(dateString);
      if (!date) return '';
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      return '';
    }
  };

  // Helper function to open in maps
  const openInMaps = (lat, lng) => {
    if (!lat || !lng) return;
    
    // iOS
    if (/(iPhone|iPad|iPod)/i.test(navigator.userAgent)) {
      window.open(`maps://maps.apple.com/?q=${lat},${lng}`);
    } 
    // Android
    else if (/android/i.test(navigator.userAgent)) {
      window.open(`geo:${lat},${lng}`);
    } 
    // Desktop
    else {
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
    }
  };
  // Combine and deduplicate activities
  const allActivities = useMemo(() => {
    const activities = [];
    
    // Add activities from Google Places
    if (camp.activities && Array.isArray(camp.activities)) {
      activities.push(...camp.activities);
    }
    
    // Add camp_activities from email scout
    if (camp.camp_activities && Array.isArray(camp.camp_activities)) {
      activities.push(...camp.camp_activities);
    }
    
    // Add user-contributed activities
    if (camp.user_activities && Array.isArray(camp.user_activities)) {
      activities.push(...camp.user_activities);
    }
    
    // Remove duplicates and filter out empty/null values
    const uniqueActivities = [...new Set(activities)]
      .filter(activity => activity && typeof activity === 'string' && activity.trim() !== '');
    
    return uniqueActivities;
  }, [camp.activities, camp.camp_activities, camp.user_activities]);

  // Get description - prefer editorial_summary over description
  const description = camp.editorial_summary || camp.description || camp.brief_description;

  // Check if Important Dates section has any content
  const hasImportantDates = !!(camp.registration_open_date || userProgram?.program_data?.registration_open_date ||
    camp.early_registration_open_date || userProgram?.program_data?.early_registration_open_date ||
    camp.early_bird_deadline || userProgram?.program_data?.early_bird_deadline ||
    camp.registration_deadline || userProgram?.program_data?.registration_deadline ||
    camp.cancellation_deadline || userProgram?.program_data?.cancellation_deadline ||
    camp.payment_due_date);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
      {/* Inline Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <strong className="font-semibold">Note:</strong> Failed to load some camp details. Some information may be missing.
            </div>
          </div>
        </div>
      )}
      {/* About This Program Section - Details tab only */}
      {!showOverviewOnly && description && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            About This Program
          </h2>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
              {description}
            </p>
          </div>
        </section>
      )}

      {/* Activities & Features Section - Details tab only */}
      {!showOverviewOnly && allActivities.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Activities & Highlights
          </h2>
          <div className="flex flex-wrap gap-2">
            {allActivities.map((activity, index) => (
              <span
                key={index}
                className="inline-flex items-center bg-purple-50 text-purple-700 rounded-full px-4 py-2 text-sm font-medium border border-purple-100"
              >
                {activity}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Location & Contact Section - Details tab only. In saved mode (userProgram set) address is hidden, so only show section when there is contact data to display. */}
      {!showOverviewOnly && (userProgram
        ? (camp.contact_phone || camp.phone || camp.contact_email || camp.email || camp.website_url || camp.website)
        : (camp.formatted_address || (typeof camp.location === 'string' ? camp.location : null) || camp.general_location || camp.contact_phone || camp.phone || camp.contact_email || camp.email || camp.website_url || camp.website)
      ) && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-6">
            Location & Contact
          </h2>

          <div className="space-y-3">
            {/* Address - Only show if NOT in saved mode (userProgram is null) */}
            {!userProgram && (camp.formatted_address || (typeof camp.location === 'string' ? camp.location : null) || camp.general_location) && (
              <div 
                className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition cursor-pointer"
                onClick={() => {
                  if (camp.coordinates_lat && camp.coordinates_lng) {
                    openInMaps(camp.coordinates_lat, camp.coordinates_lng);
                  } else if (camp.latitude && camp.longitude) {
                    openInMaps(camp.latitude, camp.longitude);
                  } else {
                    const address = camp.formatted_address || camp.location;
                    if (address) {
                      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                    }
                  }
                }}
              >
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">Address</div>
                  <div className="text-sm text-gray-700">
                    {camp.formatted_address || (typeof camp.location === 'string' ? camp.location : camp.general_location || '')}
                  </div>
                </div>
                <button
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (camp.coordinates_lat && camp.coordinates_lng) {
                      openInMaps(camp.coordinates_lat, camp.coordinates_lng);
                    } else if (camp.latitude && camp.longitude) {
                      openInMaps(camp.latitude, camp.longitude);
                    } else {
                      const address = camp.formatted_address || camp.location;
                      if (address) {
                        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
                      }
                    }
                  }}
                  aria-label="Open in maps"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Phone */}
            {(camp.contact_phone || camp.phone) && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">Phone</div>
                  <div className="text-sm text-gray-700">
                    {camp.contact_phone || camp.phone}
                  </div>
                </div>
                <a
                  href={`tel:${camp.contact_phone || camp.phone}`}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  aria-label="Call phone number"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Email */}
            {(camp.contact_email || camp.email) && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">Email</div>
                  <div className="text-sm text-brand-600 underline truncate">
                    {camp.contact_email || camp.email}
                  </div>
                </div>
                <a
                  href={`mailto:${camp.contact_email || camp.email}`}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  aria-label="Send email"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Website */}
            {(camp.website_url || camp.website) && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 mb-1">Website</div>
                  <div className="text-sm text-brand-600 underline truncate">
                    {camp.website_url || camp.website}
                  </div>
                </div>
                <a
                  href={camp.website_url || camp.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                  aria-label="Open website"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Important Dates Section - Details tab only */}
      {!showOverviewOnly && hasImportantDates && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Important Dates
          </h2>

          <div className="space-y-3">
            {/* Registration Open Date */}
            {(camp.registration_open_date || userProgram?.program_data?.registration_open_date) && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-purple-50 border border-purple-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Registration Open Date</div>
                    <div className="text-xs text-gray-600">First day registration opens</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-purple-700 ml-4 flex-shrink-0">
                  {formatDate(camp.registration_open_date || userProgram?.program_data?.registration_open_date)}
                </div>
              </div>
            )}

            {/* Early Registration Open Date */}
            {(camp.early_registration_open_date || userProgram?.program_data?.early_registration_open_date) && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-teal-50 border border-teal-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Early Registration Open Date</div>
                    <div className="text-xs text-gray-600">First day early registration opens</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-teal-700 ml-4 flex-shrink-0">
                  {formatDate(camp.early_registration_open_date || userProgram?.program_data?.early_registration_open_date)}
                </div>
              </div>
            )}

            {/* Early Bird Deadline */}
            {(camp.early_bird_deadline || userProgram?.program_data?.early_bird_deadline) && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-amber-50 border border-amber-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Early Bird Deadline</div>
                    <div className="text-xs text-gray-600">Save on registration</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-amber-700 ml-4 flex-shrink-0">
                  {formatDate(camp.early_bird_deadline || userProgram?.program_data?.early_bird_deadline)}
                </div>
              </div>
            )}

            {/* Registration Deadline */}
            {(camp.registration_deadline || userProgram?.program_data?.registration_deadline) && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-blue-50 border border-blue-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <ClipboardCheck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Registration Deadline</div>
                    <div className="text-xs text-gray-600">Last day to register</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-blue-700 ml-4 flex-shrink-0">
                  {formatDate(camp.registration_deadline || userProgram?.program_data?.registration_deadline)}
                </div>
              </div>
            )}

            {/* Cancellation Deadline */}
            {(camp.cancellation_deadline || userProgram?.program_data?.cancellation_deadline) && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-red-50 border border-red-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Cancellation Deadline</div>
                    <div className="text-xs text-gray-600">Last day for full refund</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-red-700 ml-4 flex-shrink-0">
                  {formatDate(camp.cancellation_deadline || userProgram?.program_data?.cancellation_deadline)}
                </div>
              </div>
            )}

            {/* Payment Due Date */}
            {camp.payment_due_date && (
              <div className={cn(
                "flex items-center justify-between p-4 rounded-lg",
                "bg-orange-50 border border-orange-200"
              )}>
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1 ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900">Payment Due Date</div>
                    <div className="text-xs text-gray-600">Payment deadline</div>
                  </div>
                </div>
                <div className="text-base font-semibold text-orange-700 ml-4 flex-shrink-0">
                  {formatDate(camp.payment_due_date)}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Camp Sessions Section - Details tab only */}
      {!showOverviewOnly && (isLoadingSessions || communitySessions.length > 0 || parentIdForSessions) && (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-900">
              Camp Sessions
            </h2>
            {/* Add Session button - only show for parent camps */}
            {parentIdForSessions && camp?.hierarchy_level !== 'session' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddSessionModal(true);
                }}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Session
              </Button>
            )}
          </div>
          {isLoadingSessions ? (
            <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-gray-600 text-sm">Loading sessions...</p>
            </div>
          ) : communitySessions.length > 0 ? (
            <div className="space-y-3">
              {communitySessions.map((session) => {
                const parseDate = (dateStr) => {
                  if (!dateStr) return null;
                  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                    const [year, month, day] = dateStr.split('-').map(Number);
                    return new Date(year, month - 1, day);
                  }
                  return new Date(dateStr);
                };

                const startDate = parseDate(session.startDate);
                const endDate = parseDate(session.endDate);

                return (
                  <div
                    key={session.id || session.place_id}
                    className="flex items-center justify-between p-4 rounded-lg bg-purple-50 border border-purple-200"
                  >
                    <div className="flex items-start flex-1 min-w-0">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Tent className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 ml-3 min-w-0">
                        <div className="text-sm font-medium text-gray-900 line-clamp-2 break-words">
                          {session.sessionName}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {startDate && endDate
                            ? `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`
                            : 'Dates not available'}
                          {session.sessionYear && ` • Year: ${session.sessionYear}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ml-4">
                      {session.price && (
                        <div className="text-base font-semibold text-purple-700 flex-shrink-0">
                          ${parseFloat(session.price).toFixed(2)}
                        </div>
                      )}
                      {session.isPlanned ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 flex-shrink-0"
                          disabled
                        >
                          <Check className="w-4 h-4 mr-1" />
                          On Your Plan
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedSession(session);
                            setShowAddToPlanModal(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add to Plan
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-6 bg-gray-50 rounded-xl border border-gray-200 text-center">
              <p className="text-gray-600 text-sm mb-2">No sessions available yet.</p>
              <p className="text-xs text-gray-500">Click "Add Session" to add the first one.</p>
            </div>
          )}
        </section>
      )}

      {/* PHASE 5: Add Session now navigates to page instead of modal */}

      {/* Add to Plan Modal */}
      <AddToPlanModal
        show={showAddToPlanModal}
        onClose={() => {
          setShowAddToPlanModal(false);
          setSelectedSession(null);
        }}
        session={selectedSession}
        children={profile?.children || []}
        onConfirm={async ({ sessionId, children, status }) => {
          try {
            const user = await getCurrentUser();
            if (!user) {
              toast({
                title: 'Error',
                description: 'Please log in to add sessions to your plan',
                variant: 'destructive',
              });
              return;
            }

            // Validate at least one child (modal also enforces this)
            const selectedChildren = Array.isArray(children) ? children : [];
            if (selectedChildren.length === 0) {
              toast({
                title: 'Select at least one child',
                description: 'Please select which child(ren) are attending this session.',
                variant: 'destructive',
              });
              return;
            }

            const validStatus = status && ['considering', 'booked'].includes(status) ? status : 'considering';

            const sessionData = selectedSession || {};
            const sessionName = sessionData.session ||
              sessionData.sessionName ||
              sessionData.name ||
              'Session';

            // CRITICAL: Use session's id (camp_audit_data row id), not parent's place_id
            const sessionDbId = sessionData.id || sessionId;
            console.log('🎯 Adding session to plan:', {
              sessionId: sessionDbId,
              sessionName,
              status: validStatus,
              childrenCount: selectedChildren.length,
              parentPlaceId: sessionData.parent_place_id || camp?.place_id,
            });

            const { error } = await supabase
              .from('user_camp_sessions')
              .insert({
                user_id: user.id,
                camp_id: sessionDbId,
                session_name: sessionName,
                session_year: sessionData.sessionYear ?? sessionData.session_year ?? new Date().getFullYear(),
                cost: sessionData.cost ?? sessionData.price ?? sessionData.session_price ?? null,
                user_camp_start_date: sessionData.user_camp_start_date || sessionData.startDate || sessionData.start_date || sessionData.camp_start_date || null,
                user_camp_end_date: sessionData.user_camp_end_date || sessionData.endDate || sessionData.end_date || sessionData.camp_end_date || null,
                status: validStatus,
                children: selectedChildren,
              });

            if (error) throw error;

            console.log('✅ Session added to plan successfully');
            toast({
              title: 'Session added to plan',
              description: `"${sessionName}" has been added to your summer plan as ${validStatus === 'booked' ? 'booked' : 'considering'}.`,
            });

            setShowAddToPlanModal(false);
            setSelectedSession(null);

            await handleSessionAdded();
          } catch (error) {
            console.error('❌ Error adding session to plan:', error);
            toast({
              title: 'Error',
              description: error.message || 'Failed to add session to plan. Please try again.',
              variant: 'destructive',
            });
          }
        }}
      />

      {/* Refund Policy Section - Details tab only */}
      {!showOverviewOnly && (camp.refund_policy || userProgram?.program_data?.refund_policy) && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Refund Policy
          </h2>
          <div className="p-6 bg-white rounded-xl border border-gray-200">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
              {camp.refund_policy || userProgram?.program_data?.refund_policy}
            </div>
          </div>
        </section>
      )}

      {/* Refund Policy Section - Placeholder when no policy exists (only in discovery mode) */}
      {!showOverviewOnly && !userProgram && !(camp.refund_policy || userProgram?.program_data?.refund_policy) && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Refund Policy
          </h2>
          <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
            <p className="text-gray-600 text-sm">
              Contact the program for more details about their refund policy.
            </p>
          </div>
        </section>
      )}

      {/* Cancellation Policy Section - Details tab only */}
      {!showOverviewOnly && camp.cancellation_policy && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Cancellation Policy
          </h2>
          <div className="p-6 bg-white rounded-xl border border-gray-200">
            <div className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
              {camp.cancellation_policy}
            </div>
          </div>
        </section>
      )}


      {/* Community Verification Section - Details tab only */}
      {!showOverviewOnly && camp.has_community_enhancements && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Community Verified
          </h2>

          <div className="p-6 bg-white rounded-xl border border-gray-200">
            <div className="text-sm text-gray-900 mb-4">
              {camp.community_contribution_count || 0} {camp.community_contribution_count === 1 ? 'parent has' : 'parents have'} 
              {' '}helped verify and enhance this camp's information.
            </div>

            {camp.data_confidence_score !== undefined && camp.data_confidence_score !== null && (
              <div>
                <div className="flex items-center justify-between text-xs text-gray-700 mb-2">
                  <span className="font-medium">Data Quality Score</span>
                  <span className="font-semibold">{camp.data_confidence_score}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, camp.data_confidence_score))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Reviews Section - Details tab only */}
      {!showOverviewOnly && camp.reviews && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900 mb-4">
            Reviews
          </h2>
          
          {(() => {
            // Parse reviews if it's a JSON string
            let reviewsArray = [];
            try {
              if (typeof camp.reviews === 'string') {
                reviewsArray = JSON.parse(camp.reviews);
              } else if (Array.isArray(camp.reviews)) {
                reviewsArray = camp.reviews;
              }
            } catch (error) {
              console.error('Error parsing reviews:', error);
              return null;
            }

            if (!reviewsArray || reviewsArray.length === 0) {
              return (
                <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                  <p className="text-gray-600 text-sm">
                    No reviews available yet.
                  </p>
                </div>
              );
            }

            // Format date helper
            const formatReviewDate = (time) => {
              if (!time) return '';
              try {
                // Handle Unix timestamp (seconds)
                if (typeof time === 'number') {
                  const date = new Date(time * 1000);
                  return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric'
                  });
                }
                // Handle ISO string
                if (typeof time === 'string') {
                  const date = new Date(time);
                  if (!isNaN(date.getTime())) {
                    return date.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    });
                  }
                }
                return '';
              } catch {
                return '';
              }
            };

            return (
              <div className="space-y-4">
                {reviewsArray.slice(0, 5).map((review, index) => (
                  <div 
                    key={index}
                    className="p-5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Author Avatar */}
                      {review.profile_photo_url ? (
                        <img
                          src={review.profile_photo_url}
                          alt={review.author_name || 'Reviewer'}
                          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-12 h-12 rounded-full bg-gradient-to-br from-purple-100 to-teal-100 flex items-center justify-center flex-shrink-0 ${review.profile_photo_url ? 'hidden' : ''}`}
                        style={{ display: review.profile_photo_url ? 'none' : 'flex' }}
                      >
                        <span className="text-lg font-semibold text-gray-700">
                          {(review.author_name || 'A')[0].toUpperCase()}
                        </span>
                      </div>

                      {/* Review Content */}
                      <div className="flex-1 min-w-0">
                        {/* Author Name and Rating */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-gray-900">
                              {review.author_name || 'Anonymous'}
                            </h4>
                            {review.author_url && (
                              <a
                                href={review.author_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-gray-600 transition"
                                aria-label={`View ${review.author_name}'s profile`}
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                          {/* Star Rating */}
                          {review.rating && (
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= review.rating
                                      ? 'text-yellow-400 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Review Text */}
                        {(() => {
                          // Handle both string and object formats (Places API v1 uses structured text)
                          // Object format: {text: "...", languageCode: "en"}
                          let reviewText = '';
                          if (typeof review.text === 'string') {
                            reviewText = review.text;
                          } else if (review.text && typeof review.text === 'object') {
                            // Handle Places API v1 format: {text: "...", languageCode: "en"}
                            reviewText = review.text.text || review.text.overview || review.text.description || '';
                          }
                          
                          return reviewText ? (
                            <p className="text-gray-700 leading-relaxed mb-3 text-sm sm:text-base">
                              {reviewText}
                            </p>
                          ) : null;
                        })()}

                        {/* Review Date */}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {review.relative_time_description && (
                            <span>{review.relative_time_description}</span>
                          )}
                          {review.time && review.relative_time_description && (
                            <span>•</span>
                          )}
                          {review.time && (
                            <span>{formatReviewDate(review.time)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Show more indicator if there are more reviews */}
                {reviewsArray.length > 5 && (
                  <div className="text-center pt-2">
                    <p className="text-sm text-gray-500">
                      Showing 5 of {reviewsArray.length} reviews
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
        </section>
      )}

      {/* Add Session Modal */}
      <AddSessionModal
        isOpen={showAddSessionModal}
        onClose={() => setShowAddSessionModal(false)}
        parentCamp={{
          id: camp?.place_id || camp?.id,
          place_id: camp?.place_id || camp?.id,
          name: camp?.name,
          organization: camp?.organization,
          program: camp?.program,
          source: camp?.source || 'user_entry',
          location: camp?.location || camp?.formatted_address,
        }}
        onSessionAdded={async (newSession, parentPlaceId) => {
          // Refresh sessions list
          await handleSessionAdded(newSession);
        }}
      />
    </div>
  );
};

export default PublicInfoSection;
